import React, { useState, useRef } from 'react';
import { Upload, FileText, CheckCircle, XCircle, Download, X, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { useImportDivisionWorkflows } from '../../hooks/use-division-workflows';
import { useWorkflowTemplates } from '../../hooks/use-workflow-templates';
import { useModalA11y } from '../../hooks/use-modal-a11y';
import type { WorkflowImportResult } from '../../services/division-workflow-service';

interface WorkflowImportModalProps {
  divisionId: string;
  isOpen: boolean;
  onClose: () => void;
  onImportComplete?: () => void;
}

export function WorkflowImportModal({
  divisionId,
  isOpen,
  onClose,
  onImportComplete,
}: WorkflowImportModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [createUsers, setCreateUsers] = useState(true);
  const [templateId, setTemplateId] = useState<string>('');
  const [applyTemplateAttributes, setApplyTemplateAttributes] = useState(false);
  const [importResult, setImportResult] = useState<WorkflowImportResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importMutation = useImportDivisionWorkflows(divisionId);
  const { data: templates = [] } = useWorkflowTemplates(divisionId, undefined, isOpen);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setImportResult(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      if (files[0].name.endsWith('.csv')) {
        handleFileSelect(files[0]);
      } else {
        alert('Please select a CSV file');
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDownloadTemplate = () => {
    const template = [
      'DODAACBEABESA,WorkflowType,RoleName,Contact Email(s),QuantityEdit,ShipVia,OrderLevelOnly,RequireExplicitSubmit',
      'M00001|AB|CD,purchase_order,Approver 1,"user1@example.com,user2@example.com",0,,0,0',
      'M00001|AB|CD,purchase_order,Approver 2,user3@example.com,0,,0,0',
      'M00001|AB|CD,purchase_order,Approver 3,user4@example.com,1,,1,1',
      'M00001|AB|CD,purchase_order,Final Reviewer,"user5@example.com,user6@example.com",0,Will Call,0,0',
    ].join('\n');
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'workflow-import-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    try {
      const result = await importMutation.mutateAsync({
        file: selectedFile,
        options: {
          updateExisting,
          createUsers,
          templateId: templateId || undefined,
          applyTemplateAttributes: templateId ? applyTemplateAttributes : undefined,
        },
      });
      setImportResult(result);
      onImportComplete?.();
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed. Please check your file and try again.');
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setImportResult(null);
    setUpdateExisting(false);
    setCreateUsers(true);
    setTemplateId('');
    setApplyTemplateAttributes(false);
    onClose();
  };

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, handleClose);

  if (!isOpen) return null;

  const handleDownloadErrors = () => {
    if (!importResult || importResult.errors.length === 0) return;

    const errorsCsv = [
      'Row,DODAACBEABESA,RoleName,Error',
      ...importResult.errors.map(err =>
        `${err.rowNumber},"${err.dodaacbeabesa || ''}","${err.roleName || ''}","${err.errorMessage}"`
      ),
    ].join('\n');
    const blob = new Blob([errorsCsv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workflow-import-errors-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="workflow-import-modal-title"
        tabIndex={-1}
        className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col focus:outline-none"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700 flex items-center justify-between">
          <h2 id="workflow-import-modal-title" className="text-xl font-semibold text-secondary-900 dark:text-white">Import Workflows</h2>
          <button
            onClick={handleClose}
            className="text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {!importResult ? (
            <>
              {/* File Upload Area */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
                  Upload CSV File
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-secondary-300 dark:border-secondary-600 hover:border-secondary-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="h-8 w-8 text-primary-600" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-secondary-900 dark:text-white">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedFile(null)}
                        className="ml-4 text-secondary-500 dark:text-secondary-400 hover:text-secondary-700"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-12 w-12 text-secondary-400 mx-auto mb-3" />
                      <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-2">
                        Drag and drop your CSV file here, or click to browse
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        Format: DODAACBEABESA, WorkflowType, RoleName, Contact Email(s), QuantityEdit, ShipVia, OrderLevelOnly, RequireExplicitSubmit
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileInputChange}
                        className="hidden"
                      />
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        variant="outline"
                        size="sm"
                        className="mt-4"
                      >
                        Select File
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Options */}
              <div className="mb-6 space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-secondary-700 dark:text-secondary-200">
                    Update existing workflows (replaces steps if workflow already exists)
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={createUsers}
                    onChange={(e) => setCreateUsers(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-secondary-700 dark:text-secondary-200">
                    Create users that don't exist yet
                  </span>
                </label>
              </div>

              {/* Link to existing template */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
                  Link to Template (optional)
                </label>
                <select
                  value={templateId}
                  onChange={(e) => {
                    setTemplateId(e.target.value);
                    if (!e.target.value) setApplyTemplateAttributes(false);
                  }}
                  className="w-full rounded-md border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white px-3 py-2 text-sm"
                >
                  <option value="">No template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                  Associates every workflow created (or updated, if selected above) by this import with
                  the chosen workflow template.
                </p>
                {templateId && (
                  <label className="mt-2 flex items-center">
                    <input
                      type="checkbox"
                      checked={applyTemplateAttributes}
                      onChange={(e) => setApplyTemplateAttributes(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-secondary-700 dark:text-secondary-200">
                      Apply the template's order attributes to imported workflows
                    </span>
                  </label>
                )}
                {templateId && applyTemplateAttributes && (
                  <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                    Sets up each imported workflow's required/editable order attributes (including
                    step restrictions and any template-only custom attributes) from the selected
                    template, instead of just linking it for reference.
                  </p>
                )}
              </div>

              {/* Template & Format Info */}
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900 mb-2">
                      CSV Format
                    </p>
                    <p className="text-sm text-blue-700 mb-2">
                      Each row maps a DODAACBEABESA to a role with assigned users.
                      Rows with the same DODAACBEABESA are grouped into one workflow.
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mb-3 font-mono">
                      DODAACBEABESA,WorkflowType,RoleName,Contact Email(s),QuantityEdit,ShipVia,OrderLevelOnly,RequireExplicitSubmit
                    </p>
                    
                    {/* Column Documentation */}
                    <div className="text-xs text-blue-700 mb-3 space-y-2 bg-blue-100 dark:bg-blue-900 p-2 rounded">
                      <div>
                        <p className="font-semibold text-blue-900">Column Details:</p>
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">1. DODAACBEABESA</p>
                        <p className="text-blue-600 dark:text-blue-400">Unique identifier for the workflow. All rows with the same value form one workflow.</p>
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">2. WorkflowType</p>
                        <p className="text-blue-600 dark:text-blue-400">Workflow category (e.g., <code>purchase_order</code>, <code>invoice</code>). Defaults to <code>purchase_order</code> if empty.</p>
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">3. RoleName</p>
                        <p className="text-blue-600 dark:text-blue-400">Valid values: <code>Approver 1</code>, <code>Approver 2</code>, <code>Approver 3</code>, <code>Final Reviewer</code>. Steps are ordered by number, with Final Reviewer last.</p>
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">4. Contact Email(s)</p>
                        <p className="text-blue-600 dark:text-blue-400">Single email or comma-separated list (in quotes). Example: <code>"user1@example.com,user2@example.com"</code>. Leave blank to create the step with no approvers — it will automatically be bypassed at runtime.</p>
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">5. QuantityEdit</p>
                        <p className="text-blue-600 dark:text-blue-400">Boolean flag: <code>0</code> (cannot edit quantities) or <code>1</code> (can edit quantities). Defaults to <code>0</code>.</p>
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">6. ShipVia (optional)</p>
                        <p className="text-blue-600 dark:text-blue-400">When set, adds an additional routing rule (<code>$.ShipVia</code> contains this value) so the workflow only matches orders shipped via that method. Leave blank to skip.</p>
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">7. OrderLevelOnly (optional)</p>
                        <p className="text-blue-600 dark:text-blue-400">Boolean flag: <code>0</code> (approvers can approve individual line items) or <code>1</code> (approvers can only approve/reject the entire order for this step). Defaults to <code>0</code>.</p>
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">8. RequireExplicitSubmit (optional)</p>
                        <p className="text-blue-600 dark:text-blue-400">Boolean flag: <code>0</code> (decisions commit immediately) or <code>1</code> (approvers draft decisions with Undo/Discard and must click Submit before anything commits). Defaults to <code>0</code>.</p>
                      </div>
                    </div>

                    <div className="text-xs text-blue-600 dark:text-blue-400 mb-3">
                      <p><strong>Example:</strong> Multiple approvers for one workflow automatically become sequential steps in order.</p>
                    </div>
                    <Button
                      onClick={handleDownloadTemplate}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download CSV Template
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Import Results */
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-secondary-50 dark:bg-secondary-900 rounded-lg">
                  <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-1">CSV Rows</p>
                  <p className="text-2xl font-semibold text-secondary-900 dark:text-white">
                    {importResult.summary.totalRecords}
                  </p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                  <p className="text-sm text-green-600 dark:text-green-400 mb-1">Workflows Created</p>
                  <p className="text-2xl font-semibold text-green-900">
                    {importResult.summary.workflowsCreated}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">Steps Created</p>
                  <p className="text-2xl font-semibold text-blue-900">
                    {importResult.summary.stepsCreated}
                  </p>
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400 mb-1">Errors</p>
                  <p className="text-2xl font-semibold text-red-900">
                    {importResult.summary.errorCount}
                  </p>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-secondary-50 dark:bg-secondary-900 rounded-lg text-center">
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Updated</p>
                  <p className="text-lg font-semibold">{importResult.summary.workflowsUpdated}</p>
                </div>
                <div className="p-3 bg-secondary-50 dark:bg-secondary-900 rounded-lg text-center">
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Users Created</p>
                  <p className="text-lg font-semibold">{importResult.summary.usersCreated}</p>
                </div>
                <div className="p-3 bg-secondary-50 dark:bg-secondary-900 rounded-lg text-center">
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Users Linked</p>
                  <p className="text-lg font-semibold">{importResult.summary.userRolesAssigned}</p>
                </div>
              </div>

              {/* Success Message */}
              {importResult.summary.errorCount === 0 && (
                <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <p className="text-sm text-green-900">
                    All workflows imported successfully!
                  </p>
                </div>
              )}

              {/* Imported Workflows */}
              {importResult.workflows.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-secondary-900 dark:text-white mb-3">
                    Imported Workflows ({importResult.workflows.length})
                  </h3>
                  <div className="max-h-48 overflow-y-auto border border-secondary-200 dark:border-secondary-700 rounded-lg">
                    <table className="min-w-full divide-y divide-secondary-200">
                      <thead className="bg-secondary-50 dark:bg-secondary-900 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 dark:text-secondary-400">
                            DODAACBEABESA
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 dark:text-secondary-400">
                            Steps
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 dark:text-secondary-400">
                            Users
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200">
                        {importResult.workflows.map((wf, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 text-sm font-mono text-secondary-900 dark:text-white">
                              {wf.dodaacbeabesa}
                            </td>
                            <td className="px-4 py-2 text-sm text-secondary-600 dark:text-secondary-400">
                              {wf.stepsImported}
                            </td>
                            <td className="px-4 py-2 text-sm text-secondary-600 dark:text-secondary-400">
                              {wf.usersLinked}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors */}
              {importResult.errors.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-secondary-900 dark:text-white">
                      Errors ({importResult.errors.length})
                    </h3>
                    <Button onClick={handleDownloadErrors} variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Download Errors
                    </Button>
                  </div>
                  <div className="max-h-64 overflow-y-auto border border-red-200 dark:border-red-800 rounded-lg">
                    <table className="min-w-full divide-y divide-red-200">
                      <thead className="bg-red-50 dark:bg-red-950 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-600 dark:text-red-400">
                            Row
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-600 dark:text-red-400">
                            DODAACBEABESA
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-600 dark:text-red-400">
                            Error
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-secondary-800 divide-y divide-red-200">
                        {importResult.errors.map((error, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 text-sm text-secondary-900 dark:text-white">
                              {error.rowNumber}
                            </td>
                            <td className="px-4 py-2 text-sm font-mono text-secondary-900 dark:text-white">
                              {error.dodaacbeabesa || '-'}
                            </td>
                            <td className="px-4 py-2 text-sm text-red-600 dark:text-red-400">
                              {error.errorMessage}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-secondary-200 dark:border-secondary-700 flex justify-end gap-3">
          {!importResult ? (
            <>
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={!selectedFile || importMutation.isPending}
              >
                {importMutation.isPending ? 'Importing...' : 'Import Workflows'}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>Close</Button>
          )}
        </div>
      </div>
    </div>
  );
}
